import React, { useState } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Grid, Link } from 'components/ui';
import ProductImage from 'components/Product/ProductImage/ProductImage';
import Markdown from 'components/Markdown/Markdown';
import { colors, space } from 'style/config';
import LanguageLocale from 'utils/LanguageLocale';
import EN from 'components/GlobalModals/InventoryAwareStorePickerModal/locales/InventoryAwareStorePickerModal_en_US';
import FR from 'components/GlobalModals/InventoryAwareStorePickerModal/locales/InventoryAwareStorePickerModal_fr_CA';

const { getLocalisedDataFromResource } = LanguageLocale;
const getText = getLocalisedDataFromResource(EN, FR);

const MAX_ITEMS = 5;
const MAX_ITEMS_PER_ROW = MAX_ITEMS + 1;
const GRID_GAP = 2; // space[2] = 8px
const IMG_SIZE = 40;

const InventoryItem = ({ item }) => (
    <Box css={styles.box}>
        <ProductImage
            id={item.skuId}
            disableLazyLoad
            skuImages={item.skuImages}
            size={IMG_SIZE}
            altText='' // TO DO
            showPlaceholderOnError
            opacity={item.outOfStockTreatment ? 0.5 : 1}
        />

        {item.qty > 1 && (
            <div css={styles.quantityContainer}>
                <span>{item.qty}</span>
            </div>
        )}

        {item.outOfStockTreatment && (
            <svg css={styles.oos}>
                <line
                    x1={0}
                    y1='100%'
                    x2='100%'
                    y2={0}
                />
            </svg>
        )}
    </Box>
);

function InventoryGrid({ storePickupInventory }) {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!storePickupInventory || !storePickupInventory.items) {
        return null;
    }

    const totalItems = storePickupInventory.items.length;
    const hasMoreItems = totalItems > MAX_ITEMS;
    const itemsToDisplay = isExpanded ? storePickupInventory.items : storePickupInventory.items.slice(0, MAX_ITEMS);
    const showSeeAll = hasMoreItems && !isExpanded;

    const calculateGridWidth = () => {
        const gapCount = MAX_ITEMS_PER_ROW - 1;

        return `calc(${IMG_SIZE}px * ${MAX_ITEMS_PER_ROW} + ${gapCount} * ${space[GRID_GAP]}px)`;
    };

    const handleSeeMoreClick = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsExpanded(true);
    };

    const getAvailabilityTextColor = () => {
        const { itemAvailableCount, itemCount } = storePickupInventory;

        if (itemAvailableCount === 0) {
            return colors.red;
        }

        if (itemAvailableCount === itemCount) {
            return colors.green;
        }

        return colors.black;
    };

    return (
        <div>
            {storePickupInventory.itemAvailableCount > 0 && (
                <Grid
                    columns={`repeat(${MAX_ITEMS_PER_ROW}, 1fr)`}
                    gap={GRID_GAP}
                    maxWidth={calculateGridWidth()}
                    justifyItems='center'
                    alignItems='center'
                >
                    {itemsToDisplay.map(item => (
                        <InventoryItem
                            key={item.skuId}
                            item={item}
                        />
                    ))}
                    {showSeeAll && (
                        <Link
                            width={56}
                            height={40}
                            paddingLeft={2}
                            disableUnderline
                            textAlign='center'
                            fontSize='sm'
                            color='gray'
                            onClick={handleSeeMoreClick}
                            css={styles.seeAll}
                        >
                            {getText('seeAllItems', [totalItems])}
                        </Link>
                    )}
                </Grid>
            )}
            <Markdown
                marginTop={storePickupInventory.itemAvailableCount > 0 ? 1 : 0}
                color={getAvailabilityTextColor()}
                content={getText('availableFromPickupBasket', [storePickupInventory.itemAvailableCount, storePickupInventory.itemCount])}
            />
        </div>
    );
}

const styles = {
    quantityContainer: {
        minWidth: 20,
        paddingLeft: 4,
        paddingRight: 4,
        height: 20,
        background: 'white',
        border: `1px solid ${colors.lightGray}`,
        position: 'absolute',
        borderRadius: 32,
        bottom: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11
    },
    box: {
        position: 'relative',
        flex: 'none',
        width: IMG_SIZE
    },
    oos: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '100%',
        height: '100%',
        padding: 3,
        transform: 'translate(-50%, -50%)',
        filter: `drop-shadow(0 0 1.75px ${colors.white})`,
        '> line': {
            stroke: colors.black,
            strokeWidth: 2
        }
    },
    seeAll: {
        borderLeft: `1px solid ${colors.lightGray}`,
        cursor: 'pointer'
    }
};

export default wrapFunctionalComponent(InventoryGrid, 'InventoryGrid');
